import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { UnitPills } from "../components/UnitPills";
import { IngredientPicker } from "../components/IngredientPicker";
import { api, formatApiError } from "../lib/api";
import { RECIPE_CATEGORIES, euro, catColor, foodCostColor } from "../lib/constants";
import { computeRecipeCost } from "../lib/calc";
import { Trash2, Save, ArrowLeft, Plus, X } from "lucide-react";

const EMPTY = {
    name: "",
    category: "Primi",
    portions: 4,
    selling_price: 0,
    notes: "",
    ingredients: [],
};

export default function RecipeDetail() {
    const { id } = useParams();
    const isNew = id === "new";
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [allIngredients, setAllIngredients] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        api.get("/ingredients").then(({ data }) => setAllIngredients(data));
    }, []);

    useEffect(() => {
        if (isNew) return;
        api.get(`/recipes/${id}`)
            .then(({ data }) => setForm({
                name: data.name || "",
                category: data.category,
                portions: data.portions || 1,
                selling_price: data.selling_price ?? 0,
                notes: data.notes || "",
                ingredients: data.ingredients || [],
            }))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const ingById = useMemo(() => {
        const m = {};
        for (const i of allIngredients) m[i.id] = i;
        return m;
    }, [allIngredients]);

    const costs = useMemo(() => computeRecipeCost(form, ingById), [form, ingById]);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const addIngredient = (ing) => {
        setForm((f) => ({
            ...f,
            ingredients: [...f.ingredients, { ingredient_id: ing.id, quantity: 100, unit: ing.unit === "pz" ? "pz" : (ing.unit === "l" ? "ml" : "g") }],
        }));
    };

    const updateLine = (idx, patch) =>
        setForm((f) => ({
            ...f,
            ingredients: f.ingredients.map((x, i) => (i === idx ? { ...x, ...patch } : x)),
        }));

    const removeLine = (idx) =>
        setForm((f) => ({
            ...f,
            ingredients: f.ingredients.filter((_, i) => i !== idx),
        }));

    const save = async () => {
        setError("");
        if (!form.name.trim()) { setError("Il nome è obbligatorio."); return; }
        if (Number(form.portions) < 1) { setError("Le porzioni devono essere almeno 1."); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                portions: Number(form.portions) || 1,
                selling_price: Number(form.selling_price) || 0,
                ingredients: form.ingredients.map((x) => ({
                    ingredient_id: x.ingredient_id,
                    quantity: Number(x.quantity) || 0,
                    unit: x.unit,
                })),
            };
            if (isNew) await api.post("/recipes", payload);
            else await api.put(`/recipes/${id}`, payload);
            navigate("/recipes");
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Eliminare definitivamente questo piatto?")) return;
        try {
            await api.delete(`/recipes/${id}`);
            navigate("/recipes");
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    if (loading) {
        return (
            <AppShell>
                <div className="py-16 text-center text-sm text-brand-muted">Caricamento…</div>
            </AppShell>
        );
    }

    const fcColor = foodCostColor(costs.food_cost_pct);

    return (
        <AppShell>
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink transition"
                data-testid="back-btn"
            >
                <ArrowLeft size={14} /> Indietro
            </button>

            <div className="mt-3 flex items-end justify-between gap-6 flex-wrap mb-6">
                <div>
                    <div className="eyebrow text-brand-gold mb-2">{isNew ? "Nuovo piatto" : "Modifica piatto"}</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-brand-ink leading-tight">
                        {form.name || "Senza nome"}
                    </h1>
                </div>
                {!isNew && (
                    <button
                        onClick={remove}
                        data-testid="recipe-delete"
                        className="inline-flex items-center gap-2 text-sm text-brand-danger border border-brand-danger/30 hover:bg-brand-danger/10 rounded-full px-4 py-2 transition"
                    >
                        <Trash2 size={14} /> Elimina
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-5 rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5">
                    {error}
                </div>
            )}

            {/* Live summary */}
            <div
                className="rounded-2xl p-6 sm:p-7 mb-8 text-white shadow-card overflow-hidden relative"
                style={{ background: "linear-gradient(135deg, #1a2a44 0%, #243a5e 100%)" }}
                data-testid="recipe-summary"
            >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Summary label="Costo totale" value={euro(costs.total_cost)} />
                    <Summary label="Costo/porzione" value={euro(costs.cost_per_portion)} />
                    <Summary label="Food Cost" value={costs.food_cost_pct ? `${costs.food_cost_pct.toFixed(1)}%` : "—"} accentColor={fcColor} />
                    <Summary label="Margine" value={euro(costs.margin)} />
                </div>
                <div
                    className="mt-5 rounded-xl px-5 py-4 flex items-center justify-between gap-4"
                    style={{ background: "rgba(201,169,97,0.18)", border: "1px solid rgba(201,169,97,0.35)" }}
                    data-testid="suggested-price-box"
                >
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-bold">Prezzo consigliato</div>
                        <div className="text-[11px] text-white/75 mt-0.5">per raggiungere un food cost del 30%</div>
                    </div>
                    <div className="font-display text-3xl text-white">{euro(costs.suggested_selling_price)}</div>
                </div>
            </div>

            {(costs.zero_cost_ingredient_names?.length > 0 || costs.orphan_ingredient_names?.length > 0) && (
                <div className="mb-6 space-y-2">
                    {costs.zero_cost_ingredient_names?.length > 0 && (
                        <div
                            className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
                            data-testid="recipe-zero-cost-alert"
                        >
                            <span className="text-amber-900 mt-0.5">€</span>
                            <div className="text-[13px] text-amber-900">
                                <div className="font-bold mb-0.5">
                                    {costs.zero_cost_ingredient_names.length} ingredienti senza prezzo
                                </div>
                                <div>
                                    Il food cost è <strong>sottostimato</strong> finché non imposti un costo per: {" "}
                                    <span className="font-semibold">
                                        {costs.zero_cost_ingredient_names.join(", ")}
                                    </span>
                                    . Vai in <Link to="/ingredients" className="underline font-semibold">Ingredienti</Link> e aggiorna i prezzi.
                                </div>
                            </div>
                        </div>
                    )}
                    {costs.orphan_ingredient_names?.length > 0 && (
                        <div
                            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3"
                            data-testid="recipe-orphan-alert"
                        >
                            <span className="text-brand-danger mt-0.5">!</span>
                            <div className="text-[13px] text-brand-danger">
                                <div className="font-bold mb-0.5">
                                    {costs.orphan_ingredient_names.length} righe escluse dal food cost
                                </div>
                                <div>
                                    Unità di misura incompatibile o ingrediente cancellato: {" "}
                                    <span className="font-semibold">
                                        {costs.orphan_ingredient_names.join(", ")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Form */}
            <div className="bg-paper rounded-2xl border border-brand-line shadow-card p-6 sm:p-8 space-y-6">
                <div>
                    <Label>Nome</Label>
                    <input
                        data-testid="rec-name"
                        value={form.name}
                        onChange={(e) => set("name")(e.target.value)}
                        placeholder="Es. Spaghetti alla Carbonara"
                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                    />
                </div>

                <div>
                    <Label>Categoria</Label>
                    <div className="flex flex-wrap gap-2" data-testid="rec-categories">
                        {RECIPE_CATEGORIES.map((c) => {
                            const active = form.category === c;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    data-testid={`rec-cat-${c}`}
                                    onClick={() => set("category")(c)}
                                    className={`px-4 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-wider border transition ${
                                        active
                                            ? "bg-brand-navy text-white border-brand-navy"
                                            : "bg-white text-brand-ink/70 border-brand-line hover:border-brand-gold/50"
                                    }`}
                                >
                                    {c}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                        <Label>Porzioni</Label>
                        <input
                            data-testid="rec-portions"
                            type="number"
                            min="1"
                            value={form.portions}
                            onChange={(e) => set("portions")(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                        />
                    </div>
                    <div>
                        <Label>Prezzo di vendita (€)</Label>
                        <input
                            data-testid="rec-selling-price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.selling_price}
                            onChange={(e) => set("selling_price")(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                        />
                    </div>
                </div>

                {/* Ingredients */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <Label>Ingredienti ({form.ingredients.length})</Label>
                        <button
                            type="button"
                            data-testid="rec-add-ingredient"
                            onClick={() => setPickerOpen(true)}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy hover:text-brand-gold transition"
                        >
                            <Plus size={14} /> Aggiungi
                        </button>
                    </div>

                    <div className="space-y-2" data-testid="rec-ingredients-list">
                        {form.ingredients.map((line, idx) => {
                            const ing = ingById[line.ingredient_id];
                            if (!ing) {
                                return (
                                    <div key={idx} className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-brand-danger flex items-center justify-between">
                                        Ingrediente non trovato
                                        <button onClick={() => removeLine(idx)}><X size={14} /></button>
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={idx}
                                    className="rounded-lg bg-white border border-brand-line flex items-center gap-3 px-3 py-2.5 hover:border-brand-gold/40 transition"
                                    data-testid={`rec-line-${idx}`}
                                >
                                    <span className="w-1 h-10 rounded-full shrink-0" style={{ background: catColor(ing.category) }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-brand-ink truncate">{ing.name}</div>
                                        <div className="text-[11px] text-brand-muted">
                                            {Number(ing.cost_per_unit).toFixed(3).replace(".", ",")} €/{ing.unit} · scarto {ing.waste_pct || 0}%
                                        </div>
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={line.quantity}
                                        onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                                        data-testid={`rec-line-qty-${idx}`}
                                        className="w-20 px-2 py-1.5 rounded-md bg-brand-cream border border-brand-line text-sm text-right focus:outline-none focus:border-brand-gold"
                                    />
                                    <UnitPills value={line.unit} onChange={(u) => updateLine(idx, { unit: u })} testid={`rec-line-unit-${idx}`} />
                                    <button
                                        onClick={() => removeLine(idx)}
                                        data-testid={`rec-line-remove-${idx}`}
                                        className="text-brand-muted hover:text-brand-danger p-1 transition"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            );
                        })}
                        {form.ingredients.length === 0 && (
                            <div className="rounded-lg bg-brand-cream border border-dashed border-brand-line px-4 py-6 text-center text-sm text-brand-muted">
                                Nessun ingrediente. Aggiungine uno per iniziare a calcolare il food cost.
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <Label>Note di preparazione</Label>
                    <textarea
                        data-testid="rec-notes"
                        value={form.notes}
                        onChange={(e) => set("notes")(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold resize-none"
                    />
                </div>
            </div>

            <div className="sticky bottom-20 lg:bottom-4 mt-6 flex justify-end">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="recipe-save"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-7 py-3 text-sm font-semibold hover:bg-brand-navyLight transition disabled:opacity-60 shadow-card"
                >
                    <Save size={16} />
                    {saving ? "Salvataggio…" : "Salva"}
                </button>
            </div>

            <IngredientPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                ingredients={allIngredients}
                onPick={addIngredient}
            />
        </AppShell>
    );
}

function Label({ children }) {
    return <div className="eyebrow text-brand-ink/60 mb-1.5">{children}</div>;
}

function Summary({ label, value, accentColor }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">{label}</div>
            <div
                className="mt-1 font-display text-2xl lg:text-3xl leading-none"
                style={{ color: accentColor || "#ffffff" }}
            >
                {value}
            </div>
        </div>
    );
}
